import { useId } from 'react';

import {
  RadioGroup as ShadcnRadioGroup,
  RadioGroupItem as ShadcnRadioGroupItem
} from '@/components/ui/RadioGroup';
import { cn } from '@/lib/utils';

export type RadioGroupOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type RadioGroupVariant = 'default' | 'card';

export type RadioGroupProps = {
  name: string;
  options: RadioGroupOption[];
  value?: string;
  onChange: (value: string) => void;
  orientation?: 'horizontal' | 'vertical';
  variant?: RadioGroupVariant;
  className?: string;
  disabled?: boolean;
  id?: string;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
};

export function RadioGroup({
  name,
  options,
  value,
  onChange,
  orientation = 'horizontal',
  variant = 'default',
  className,
  disabled = false,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-labelledby': ariaLabelledBy
}: RadioGroupProps) {
  // Scope the item ids to this instance so two RadioGroups with the same `name` (e.g. a guard
  // editor stacked over the dictionary editor it was opened from) don't produce duplicate DOM ids,
  // which would make a `<label htmlFor>` bind to the wrong instance's radio.
  const instanceId = useId();

  const containerClass = cn(
    orientation === 'horizontal' && 'flex flex-wrap gap-2',
    orientation === 'vertical' && 'flex flex-col gap-2'
  );

  return (
    <ShadcnRadioGroup
      id={id}
      value={value}
      onValueChange={onChange}
      className={cn(containerClass, className)}
      disabled={disabled}
      aria-describedby={ariaDescribedBy}
      aria-labelledby={ariaLabelledBy}
    >
      {options.map((option) => {
        const isSelected = value === option.value;
        const isDisabled = disabled || option.disabled === true;
        const itemId = `${instanceId}-${name}-${option.value}`;

        if (variant === 'card') {
          return (
            <label
              key={option.value}
              htmlFor={itemId}
              className={cn(
                'relative rounded-lg border p-4 transition-colors',
                orientation === 'horizontal' && 'flex-1',
                isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-input bg-background hover:bg-accent'
              )}
            >
              <ShadcnRadioGroupItem
                value={option.value}
                id={itemId}
                className="absolute top-1/2 right-3 shrink-0 -translate-y-1/2"
                disabled={isDisabled}
              />
              <div className="space-y-1 pr-6">
                <div className="text-sm leading-none font-medium">{option.label}</div>
                {option.description ? (
                  <div className="text-sm text-muted-foreground">{option.description}</div>
                ) : null}
              </div>
            </label>
          );
        }

        return (
          <label
            key={option.value}
            htmlFor={itemId}
            className={cn(
              'flex gap-3',
              isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            )}
          >
            <div className="flex h-5 items-center">
              <ShadcnRadioGroupItem
                value={option.value}
                id={itemId}
                className="shrink-0"
                disabled={isDisabled}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm leading-5 font-medium">{option.label}</div>
              {option.description ? (
                <div className="mt-1 text-sm text-muted-foreground">{option.description}</div>
              ) : null}
            </div>
          </label>
        );
      })}
    </ShadcnRadioGroup>
  );
}
